import { FilingStatus } from "./taxCalculatorUtils";
import { OREGON_TAX_BRACKETS_2025 } from "./taxCalculatorUtils";

type LtcgBrackets = Array<{ rate: number; upper: number }>;

// Keep in sync with the app’s bracket assumptions.
const FEDERAL_LTCG_BRACKETS_2025: Record<FilingStatus, LtcgBrackets> = {
  single: [
    { rate: 0.0, upper: 48350 },
    { rate: 0.15, upper: 533400 },
    { rate: 0.2, upper: Infinity },
  ],
  married_jointly: [
    { rate: 0.0, upper: 96700 },
    { rate: 0.15, upper: 600050 },
    { rate: 0.2, upper: Infinity },
  ],
  head_of_household: [
    { rate: 0.0, upper: 64750 },
    { rate: 0.15, upper: 566700 },
    { rate: 0.2, upper: Infinity },
  ],
};

export const calculateFederalLtcgTax = (params: {
  filingStatus: FilingStatus;
  baseTaxableIncome: number;
  longTermCapitalGains: number;
}) => {
  const gains = Math.max(0, params.longTermCapitalGains);
  if (gains === 0) return 0;

  // Apply LTCG brackets based on total taxable income, with gains "stacked" on top.
  const brackets = FEDERAL_LTCG_BRACKETS_2025[params.filingStatus];

  let remaining = gains;
  let tax = 0;
  let currentTotalTaxableIncome = Math.max(0, params.baseTaxableIncome);

  for (const bracket of brackets) {
    if (remaining <= 0) break;
    const bracketUpper = bracket.upper;
    const roomInBracket = Math.max(0, bracketUpper - currentTotalTaxableIncome);
    const amountAtRate = Math.min(remaining, roomInBracket);
    tax += amountAtRate * bracket.rate;
    remaining -= amountAtRate;
    currentTotalTaxableIncome += amountAtRate;
  }

  return tax;
};

export const calculateAmountRealized = (
  salePrice: number,
  sellingCosts: number
) => salePrice - sellingCosts;

export const calculateAdjustedBasis = (
  purchasePrice: number,
  improvements: number
) => Math.max(0, purchasePrice + improvements);

export const calculateCapitalGain = (params: {
  salePrice: number;
  sellingCostsTotal: number;
  purchasePrice: number;
  improvements: number;
}) => {
  const amountRealized = calculateAmountRealized(
    params.salePrice,
    params.sellingCostsTotal
  );
  const adjustedBasis = calculateAdjustedBasis(
    params.purchasePrice,
    params.improvements
  );
  const rawGain = amountRealized - adjustedBasis;
  const gain = Math.max(0, rawGain);
  return { amountRealized, adjustedBasis, rawGain, gain };
};

const NIIT_THRESHOLDS: Record<FilingStatus, number> = {
  single: 200000,
  married_jointly: 250000,
  head_of_household: 200000,
};

const calculateProgressiveTax = (
  taxableIncome: number,
  brackets: Array<{ rate: number; lowerBound: number; upperBound: number }>
) => {
  let total = 0;
  for (const bracket of brackets) {
    if (taxableIncome <= bracket.lowerBound) break;
    const inBracket =
      Math.min(taxableIncome, bracket.upperBound) - bracket.lowerBound;
    total += inBracket * bracket.rate;
  }
  return total;
};

export const calculateHousingSaleAnalysis = (params: {
  filingStatus: FilingStatus;
  baseTaxableIncome: number;
  baseGrossIncome: number;
  salePrice: number;
  brokerCommission: number;
  closingCosts: number;
  otherSellingCosts: number;
  purchasePrice: number;
  improvements: number;
  currentMortgageBalance: number;
  mortgageInterest: number;
  reinvest1031: boolean;
  includeNIIT: boolean;
}) => {
  const sellingCostsTotal =
    Math.max(0, params.brokerCommission) +
    Math.max(0, params.closingCosts) +
    Math.max(0, params.otherSellingCosts);

  const { gain } = calculateCapitalGain({
    salePrice: params.salePrice,
    sellingCostsTotal,
    purchasePrice: params.purchasePrice,
    improvements: params.improvements,
  });

  const federalLtcgTax = params.reinvest1031
    ? 0
    : calculateFederalLtcgTax({
        filingStatus: params.filingStatus,
        baseTaxableIncome: Math.max(0, params.baseTaxableIncome),
        longTermCapitalGains: gain,
      });

  const oregonCapitalGainsTax = params.reinvest1031
    ? 0
    : (() => {
        // Approx: incremental Oregon income tax on added capital gains using the existing bracket model.
        const base = calculateProgressiveTax(
          Math.max(0, params.baseTaxableIncome),
          OREGON_TAX_BRACKETS_2025
        );
        const withGains = calculateProgressiveTax(
          Math.max(0, params.baseTaxableIncome) + Math.max(0, gain),
          OREGON_TAX_BRACKETS_2025
        );
        return Math.max(0, withGains - base);
      })();

  const niitTax =
    params.reinvest1031 || !params.includeNIIT
      ? 0
      : (() => {
          const threshold = NIIT_THRESHOLDS[params.filingStatus];
          const magiApprox =
            Math.max(0, params.baseGrossIncome) + Math.max(0, gain);
          const excess = Math.max(0, magiApprox - threshold);
          const netInvestmentIncomeApprox = Math.max(0, gain);
          const base = Math.min(excess, netInvestmentIncomeApprox);
          return base * 0.038;
        })();

  const totalTax = federalLtcgTax + oregonCapitalGainsTax + niitTax;

  const netProceedsAfterTax = Math.max(
    0,
    Math.max(0, params.salePrice) - sellingCostsTotal - totalTax
  );
  const mortgagePayoffTotal =
    Math.max(0, params.currentMortgageBalance) +
    Math.max(0, params.mortgageInterest);

  const netCashAfterMortgage = Math.max(
    0,
    netProceedsAfterTax - mortgagePayoffTotal
  );

  const netProceedsNoTax = Math.max(
    0,
    Math.max(0, params.salePrice) - sellingCostsTotal
  );
  const netCashAfterMortgageNoTax = Math.max(
    0,
    netProceedsNoTax - mortgagePayoffTotal
  );

  return {
    sellingCostsTotal,
    gain,
    federalLtcgTax,
    oregonCapitalGainsTax,
    niitTax,
    totalTax,
    mortgagePayoffTotal,
    netCashAfterMortgage,
    netCashAfterMortgageNoTax,
  };
};
